import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Checkbox,
  CircularProgress
} from '@material-ui/core';
import {
  setCopyShapesModalOpen,
  fetchCustomShapesForCopy,
  copyShapesForGroup,
} from 'store/actions';
import ClearIcon from '@material-ui/icons/Clear';
import { parseISO, format } from 'date-fns';

const CopyShapesModal = ({
  classes,
  selectedGroup,
  copyShapesModalOpen,
  setCopyShapesModalOpen,
  fetchCustomShapesForCopy,
  copyShapesForGroup,
  groupsAreLoading
}) => {
  const [localShapes, setLocalShapes] = useState([]);

  useEffect(() => {
    if (!copyShapesModalOpen) return;
    fetchCustomShapesForCopy(selectedGroup.groupId, data => setLocalShapes(data));
  }, [copyShapesModalOpen]);

  // == Event Handlers ====================================
  const handleClose = (e, reason) => {
    if (reason === 'backdropClick') return; // updated handling for disableBackdropClick
    setCopyShapesModalOpen(false);
    setLocalShapes([]);
  }

  const onSaveClicked = () => {
    const selectedShapes = localShapes
      .filter((oo) => oo.isSelected)
      .map((oo) => oo.shapeId);

    const request = {
      shapeIds: selectedShapes,
      groupId: selectedGroup.groupId,
    };

    copyShapesForGroup(request);
    handleClose();
  };

  const onShapeSelected = (e, index) => {
    const shape = localShapes[index];
    const newShape = {
      ...shape,
      isSelected: e.target.checked,
    };
    const copiedShapes = [...localShapes];
    copiedShapes[index] = newShape;
    setLocalShapes(copiedShapes);
  };

  // == Components ======================================

  return (
    <Dialog onClose={handleClose} open={copyShapesModalOpen} maxWidth='xl' fullWidth>
      <DialogTitle>Import Shapes</DialogTitle>
      <IconButton className={classes.closeButton} onClick={handleClose}>
        <ClearIcon fontSize="inherit" />
      </IconButton>
      <DialogContent style={{ paddingTop: 0 }}>
        {groupsAreLoading
          ? <div className={classes.loadingContainer}><CircularProgress/></div>
          : <table style={{ width: '100%' }}>
                <thead>
                <tr>
                  <td style={{ width: '3%', paddingBottom: 20 }}></td>
                  <td style={{ width: '17%', paddingBottom: 20, fontWeight: 'bold' }}>Shape</td>
                  <td style={{ width: '17%', paddingBottom: 20, fontWeight: 'bold' }}>Group</td>
                  <td style={{ width: '17%', paddingBottom: 20, fontWeight: 'bold' }}>Group Type</td>
                  <td style={{ width: '17%', paddingBottom: 20, fontWeight: 'bold' }}>Module</td>
                  <td style={{ width: '17%', paddingBottom: 20, fontWeight: 'bold' }}>Created By</td>
                  <td style={{ width: '11%', paddingBottom: 20, fontWeight: 'bold' }}>Published On</td>
                </tr>
              </thead>
              <tbody>
                {localShapes.map((shape, index) => {
                  return (
                    <tr key={index}>
                      <td>
                        <Checkbox
                          checked={shape.isSelected ?? false}
                          onChange={e => onShapeSelected(e, index)}
                          color='primary'
                        />
                      </td>
                      <td>{shape.shapeName}</td>
                      <td>{shape.groupName}</td>
                      <td>{shape.groupType}</td>
                      <td>{shape.moduleName}</td>
                      <td>{shape.createdBy}</td>
                      <td>{format(parseISO(shape.publishedOn), 'MM/dd/yyyy, h:mm a')}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
        }
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={onSaveClicked} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const styles = theme => ({
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-evenly',
    paddingTop: 8
  },
  flexGrow: {
    flexGrow: 1
  },
  warningContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  warningText: {
    marginLeft: 4,
    marginTop: 3
  },
  warningIcon: {
    fill: theme.palette.warning.text
  },
  closeButton: {
    position: 'absolute',
    right: 8,
    top: 8
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 100
  }
});

// == Connect ============================================

const mapStateToProps = ({ assumptionGroupsState }) => ({
  copyShapesModalOpen: assumptionGroupsState.copyShapesModalOpen,
  groupsAreLoading: assumptionGroupsState.groupsAreLoading,
  selectedGroup: assumptionGroupsState.selectedGroup
});

const mapDispatchToProps = {
  setCopyShapesModalOpen,
  fetchCustomShapesForCopy,
  copyShapesForGroup,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(CopyShapesModal);
